import NewsService, { newsImageSrc } from "../services/news.service";
import React, { useEffect, useRef, useState } from "react";

import HtmlEditor from "./HtmlEditor";
import SearchBox from "./SearchBox";
import Spinner from "./Spinner";
import { matchesQuery } from "../utils/search";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const emptyForm = {
  id: null,
  title: "",
  summary: "",
  image: "",
  content: "",
  published: true,
};

const NewsManagement = () => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all"); // all | published | draft
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const MAX_IMAGE = 2 * 1024 * 1024; // 2 MB

  const handleImageFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("news.imageInvalid", "Please select an image file."));
      e.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE) {
      toast.error(t("news.imageTooLarge", "Image must be 2 MB or smaller."));
      e.target.value = "";
      return;
    }
    setUploading(true);
    setProgress(0);
    NewsService.uploadImage(file, setProgress)
      .then((res) => {
        setForm((f) => ({ ...f, image: res.data.url }));
        toast.success(t("news.imageUploaded", "Image uploaded."));
      })
      .catch((err) =>
        toast.error((err.response && err.response.data && err.response.data.message) || err.message)
      )
      .finally(() => {
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
      });
  };

  const load = () => {
    setLoading(true);
    NewsService.getAll()
      .then((res) => setList(res.data || []))
      .catch((err) =>
        toast.error(
          (err.response && err.response.data && err.response.data.message) ||
            err.message
        )
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const edit = (n) => {
    setForm({
      id: n.id,
      title: n.title || "",
      summary: n.summary || "",
      image: n.image || "",
      content: n.content || "",
      published: !!n.published,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => setForm(emptyForm);

  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      toast.error(t("news.validation"));
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      summary: form.summary,
      image: form.image,
      content: form.content,
      published: form.published,
    };
    const action = form.id
      ? NewsService.update(form.id, payload)
      : NewsService.create(payload);

    action
      .then(() => {
        toast.success(form.id ? t("news.updated") : t("news.created"));
        reset();
        load();
      })
      .catch((err) =>
        toast.error(
          (err.response && err.response.data && err.response.data.message) ||
            err.message
        )
      )
      .finally(() => setSaving(false));
  };

  const remove = (n) => {
    if (!window.confirm(t("news.confirmDelete", { title: n.title }))) return;
    NewsService.remove(n.id)
      .then(() => {
        toast.success(t("news.deleted"));
        if (form.id === n.id) reset();
        load();
      })
      .catch((err) =>
        toast.error(
          (err.response && err.response.data && err.response.data.message) ||
            err.message
        )
      );
  };

  const filteredNews = list.filter(
    (n) =>
      (status === "all" || (status === "published") === !!n.published) &&
      matchesQuery(query, n.title, n.summary)
  );

  return (
    <div className="container my-4">
      <h3 className="mb-4">{t("news.manageTitle")}</h3>

      <div className="row g-4">
        {/* Form */}
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header fw-semibold">
              {form.id ? t("news.editNews") : t("news.newNews")}
            </div>
            <div className="card-body">
              <form onSubmit={submit}>
                <div className="mb-3">
                  <label className="form-label">{t("news.fieldTitle")}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">{t("news.fieldSummary")}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.summary}
                    onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">{t("news.fieldImage")}</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="https://…"
                    value={form.image}
                    onChange={(e) => setForm({ ...form, image: e.target.value })}
                  />
                  <div className="d-flex align-items-center gap-2 mt-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="d-none"
                      onChange={handleImageFile}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      disabled={uploading}
                      onClick={() => fileRef.current && fileRef.current.click()}
                    >
                      {uploading ? `${t("news.uploading", "Uploading…")} ${progress}%` : `⬆ ${t("news.uploadImage", "Upload image")}`}
                    </button>
                    {form.image && !uploading && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setForm({ ...form, image: "" })}
                      >
                        {t("news.removeImage", "Remove")}
                      </button>
                    )}
                    <small className="text-muted">{t("news.imageHint", "Optional · jpg, png, gif, webp · max 2 MB")}</small>
                  </div>
                  {form.image && (
                    <img
                      src={newsImageSrc(form.image)}
                      alt=""
                      className="mt-2 rounded border"
                      style={{ maxHeight: 140, maxWidth: "100%" }}
                      onError={(e) => { e.target.style.display = "none"; }}
                      onLoad={(e) => { e.target.style.display = ""; }}
                    />
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">{t("news.fieldContent")}</label>
                  <HtmlEditor
                    value={form.content}
                    onChange={(v) => setForm({ ...form, content: v })}
                    rows={8}
                  />
                </div>
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="published"
                    checked={form.published}
                    onChange={(e) =>
                      setForm({ ...form, published: e.target.checked })
                    }
                  />
                  <label className="form-check-label" htmlFor="published">
                    {t("news.published")}
                  </label>
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-primary" disabled={saving || uploading}>
                    {form.id ? t("common.update") : t("common.create")}
                  </button>
                  {form.id && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={reset}
                    >
                      {t("common.cancel")}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header fw-semibold">{t("news.allNews")}</div>
            <div className="card-body">
              {loading ? (
                <Spinner small />
              ) : list.length === 0 ? (
                <p className="text-muted mb-0">{t("news.empty")}</p>
              ) : (
                <>
                <div className="row g-2 mb-3">
                  <div className="col-md-8">
                    <SearchBox
                      value={query}
                      onChange={setQuery}
                      placeholder={t("news.searchPlaceholder", "Search by title...")}
                    />
                  </div>
                  <div className="col-md-4">
                    <select
                      className="form-select"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      aria-label={t("news.status")}
                    >
                      <option value="all">{t("news.allStatuses", "All statuses")}</option>
                      <option value="published">{t("news.published")}</option>
                      <option value="draft">{t("news.draft")}</option>
                    </select>
                  </div>
                </div>
                {filteredNews.length === 0 ? (
                  <p className="text-muted mb-0">{t("common.noResults", "No results match your search.")}</p>
                ) : (
                <div className="table-responsive rounded">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}>#</th>
                        <th>{t("news.fieldTitle")}</th>
                        <th style={{ width: 110 }}>{t("orders.date")}</th>
                        <th style={{ width: 90 }}>{t("news.status")}</th>
                        <th style={{ width: 120 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredNews.map((n) => (
                        <tr key={n.id}>
                          <td>{n.id}</td>
                          <td>{n.title}</td>
                          <td>{new Date(n.createdAt).toLocaleDateString()}</td>
                          <td>
                            <span
                              className={`badge ${
                                n.published ? "bg-success" : "bg-secondary"
                              }`}
                            >
                              {n.published
                                ? t("news.published")
                                : t("news.draft")}
                            </span>
                          </td>
                          <td className="text-nowrap">
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => edit(n)}
                            >
                              {t("common.edit")}
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => remove(n)}
                            >
                              {t("common.delete")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsManagement;
